/**
 * JardinsOleBoot — Single mounting point for all Jardins d'Olé customizations.
 * Wraps children with UniverseProvider and renders the UniverseSwitcher.
 * Also installs a scroll listener that hides the top universe banner past 80px
 * (the cockpit header then rises to the top of the viewport).
 *
 * NON-CORE FILE — additive customization. Imported once from PublicLayout.jsx.
 */
import React, { useEffect } from "react";
import { UniverseProvider } from "@/lib/jardins-ole/useUniverse";
import UniverseSwitcher from "./UniverseSwitcher";
import JOProClickCollectInjector from "./JOProClickCollectInjector";
import JOAteliersGate from "./JOAteliersGate";
import JODestockageEnhancer from "./JODestockageEnhancer";
import JOProductViewTracker from "./JOProductViewTracker";
import JOServicesSpotlightInjector from "./JOServicesSpotlightInjector";
import "@/styles/jardins-ole.css";

function useScrollClass() {
  useEffect(() => {
    let lastY = 0;
    let ticking = false;
    const onScroll = () => {
      lastY = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (lastY > 80) document.body.setAttribute("data-jdo-scrolled", "yes");
          else document.body.removeAttribute("data-jdo-scrolled");
          ticking = false;
        });
        ticking = true;
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
}

function ScrollSentinel() {
  useScrollClass();
  return null;
}

export default function JardinsOleBoot({ children }) {
  useEffect(() => {
    // Remove the anti-FOUC booting class as soon as JO theme has mounted
    // so the inline splash / neutral background gives way to the full app.
    // A small delay allows the initial paint to include universe styles.
    const timer = setTimeout(() => {
      document.documentElement.classList.remove("jo-booting");
    }, 60);
    return () => clearTimeout(timer);
  }, []);
  return (
    <UniverseProvider>
      <ScrollSentinel />
      <UniverseSwitcher />
      <JOProClickCollectInjector />
      <JOAteliersGate />
      <JODestockageEnhancer />
      <JOProductViewTracker />
      <JOServicesSpotlightInjector />
      {children}
    </UniverseProvider>
  );
}
